import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MailOpen, Check, Loader2, Send } from "lucide-react";
import { ApiService } from "../../services/api";
import type { CoachLetter } from "../../types/navigator";

// Koçun mektupları — sohbet kutusu değil, ritmi olan bir muhatap: Pazar söz, Çarşamba ara not,
// Cumartesi hesap. Cevap yalnız Pazar mektubuna verilir (söz), o da tek soruya.
const KIND_LABEL: Record<string, string> = {
  CONTRACT: "Sözleşme",
  SUNDAY: "Haftanın sözü",
  MIDWEEK: "Ara not",
  RECKONING: "Haftanın hesabı",
  CONCERN: "Endişe",
  CLOSING: "Kapanış",
  WELCOME: "Hoş geldin",
  NOTE: "Öğretmeninden not",
  REMINDER: "Hatırlatma",
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long" }) : "";

export default function CoachLetters() {
  const [letters, setLetters] = useState<CoachLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [reply, setReply] = useState<{ text: string; days: string; minutes: string }>({ text: "", days: "", minutes: "" });
  const [sending, setSending] = useState(false);
  const [planNote, setPlanNote] = useState<string | null>(null);
  // E-postadaki düğmeler mektubu buraya getirir: ?mektup=<id>&cevap=kabul|degistir. Söz yine aşağıdaki tek tıkla verilir.
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get("mektup");
  const linkedAction = searchParams.get("cevap");
  const daysInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ApiService.getCoachLetters();
      setLetters(res.items);
      const linked = linkedId ? res.items.find((l) => l.id === linkedId) : null;
      if (linked) {
        setOpenId(linked.id);
        setReply({ text: "", days: linked.ask ? String(linked.ask.days) : "", minutes: linked.ask ? String(linked.ask.minutes) : "" });
        if (!linked.readAt) ApiService.getCoachLetter(linked.id).catch(() => undefined);
      } else if (!openId && res.items.length) setOpenId(res.items[0].id);
    } catch (e: any) {
      setError(e?.message || "Mektuplar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [openId, linkedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading && linkedAction === "degistir") daysInputRef.current?.focus();
  }, [loading, linkedAction]);

  const open = async (l: CoachLetter) => {
    setOpenId(l.id);
    setReply({ text: "", days: l.ask ? String(l.ask.days) : "", minutes: l.ask ? String(l.ask.minutes) : "" });
    if (!l.readAt) {
      try {
        const fresh = await ApiService.getCoachLetter(l.id);
        setLetters((prev) => prev.map((x) => (x.id === l.id ? { ...x, readAt: fresh.readAt } : x)));
      } catch {
        /* okundu işareti kritik değil */
      }
    }
  };

  const answer = async (l: CoachLetter, accept: boolean) => {
    setSending(true);
    setError(null);
    try {
      const days = Number(reply.days) || undefined;
      const minutes = Number(reply.minutes) || undefined;
      const res = await ApiService.answerCoachLetter(l.id, { text: reply.text, accept, days, minutes });
      setLetters((prev) => prev.map((x) => (x.id === l.id ? res.letter : x)));
      if (res.ask) {
        setPlanNote(res.plan
          ? `Planın bu söze göre yeniden dağıtıldı: haftada ${res.ask.days} gün, günde ${res.ask.minutes} dakika. Konu sırası aynı kaldı.`
          : `Sözün kaydedildi: haftada ${res.ask.days} gün, günde ${res.ask.minutes} dakika.`);
      }
    } catch (e: any) {
      setError(e?.message || "Cevap gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-16">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!letters.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
        <Mail className="mx-auto mb-2 h-8 w-8 text-gray-400" />
        <p className="text-sm font-medium text-gray-900">Henüz mektup yok</p>
        <p className="mt-1 text-xs text-gray-500">Koçun Pazar akşamları yazar; Çarşamba kısa bir not, Cumartesi haftanın hesabı gelir.</p>
      </div>
    );
  }

  const current = letters.find((l) => l.id === openId) || letters[0];
  const canAnswer = current && (current.kind === "CONTRACT" || current.kind === "SUNDAY") && current.status !== "ANSWERED" && !!current.ask;

  return (
    <div className="grid gap-4 md:grid-cols-[260px_1fr]">
      {/* Liste */}
      <div className="space-y-1.5">
        {letters.map((l) => {
          const active = l.id === current?.id;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => open(l)}
              className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${active ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}
            >
              <div className="flex items-center gap-2">
                {l.readAt ? <MailOpen className="h-3.5 w-3.5 shrink-0 text-gray-400" /> : <Mail className="h-3.5 w-3.5 shrink-0 text-blue-600" />}
                <span className={`truncate text-sm ${l.readAt ? "text-gray-700" : "font-semibold text-gray-900"}`}>{l.subject}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
                <span>{KIND_LABEL[l.kind] || l.kind}</span>
                <span>·</span>
                <span>{fmtDate(l.sentAt)}</span>
                {l.status === "ANSWERED" && <Check className="h-3 w-3 text-emerald-600" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Mektup */}
      {current && (
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-base font-semibold text-gray-900">{current.subject}</h3>
            <span className="text-xs text-gray-500">
              {KIND_LABEL[current.kind] || current.kind} · {fmtDate(current.sentAt)}
            </span>
          </div>
          <div className="whitespace-pre-line text-[15px] leading-relaxed text-gray-800">{current.body}</div>

          {current.status === "ANSWERED" && current.answer && (
            <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              <span className="font-medium">Cevabın:</span>{" "}
              {current.answer.accept === true
                ? `söz kabul${current.answer.days ? ` — ${current.answer.days} gün × ${current.answer.minutes} dk` : ""}`
                : current.answer.accept === false
                ? `değiştirdin — ${current.answer.days} gün × ${current.answer.minutes} dk`
                : "yazdın"}
              {current.answer.text ? ` · "${current.answer.text}"` : ""}
            </div>
          )}

          {planNote && current.status === "ANSWERED" && (
            <p className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">{planNote}</p>
          )}

          {canAnswer && (
            <div className={`mt-5 rounded-md border p-3 ${linkedId === current.id && linkedAction ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-gray-50"}`}>
              {linkedId === current.id && linkedAction === "kabul" && (
                <p className="mb-2 text-sm text-blue-900">E-postadaki bağlantıdan geldin. Sözü vermek için aşağıdaki <b>Uyuyor, söz</b> düğmesine bas.</p>
              )}
              {linkedId === current.id && linkedAction === "degistir" && (
                <p className="mb-2 text-sm text-blue-900">Gün ve dakikayı sana uyacak şekilde değiştir, sonra <b>Böyle olsun</b>'a bas.</p>
              )}
              <p className="text-sm font-medium text-gray-900">
                Söz: {current.ask!.days} gün × {current.ask!.minutes} dakika — uyuyor mu?
              </p>
              <p className="mt-0.5 text-xs text-gray-500">Uymuyorsa gün ve dakikayı değiştir; koç takvimini buna göre yeniden dağıtır, konu sırası aynı kalır.</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="text-xs text-gray-600">
                  Gün
                  <input
                    ref={daysInputRef}
                    type="number"
                    min={1}
                    max={7}
                    value={reply.days}
                    onChange={(e) => setReply({ ...reply, days: e.target.value })}
                    className="ml-1 w-14 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </label>
                <label className="text-xs text-gray-600">
                  Dakika
                  <input
                    type="number"
                    min={15}
                    max={600}
                    step={15}
                    value={reply.minutes}
                    onChange={(e) => setReply({ ...reply, minutes: e.target.value })}
                    className="ml-1 w-16 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </label>
              </div>
              <textarea
                value={reply.text}
                onChange={(e) => setReply({ ...reply, text: e.target.value })}
                placeholder="İstersen bir satır yaz (zorunlu değil)"
                rows={2}
                className="mt-2 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={sending}
                  onClick={() => answer(current, true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Uyuyor, söz
                </button>
                <button
                  type="button"
                  disabled={sending}
                  onClick={() => answer(current, false)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  Böyle olsun (değiştirdim)
                </button>
              </div>
            </div>
          )}
          {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
